import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { formatCurrency } from '@/utils/format';
import { ProjectYearlyCosts } from '@shared/interfaces/projectYearlyCosts.interface';
import { DEFAULT_PROJECT_YEARLY_COSTS, normalizeYearlyCosts } from '@shared/constants/projectYearlyCosts';

const FINANCIAL_YEARS = ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'];

type FinancialInputs = {
  software: number[];
  services: number[];
  partnerServices: number[];
  ramp: number[];
};

interface FinancialMetricsTableProps {
  value?: ProjectYearlyCosts | null;
  onChange?: (value: ProjectYearlyCosts) => void;
  fallback?: ProjectYearlyCosts;
}

const parseCurrencyValue = (value: string): number => {
  if (!value || value.trim() === '') return 0;
  return parseInt(value.replace(/[^0-9.-]/g, ''), 10) || 0;
};

const FinancialMetricsTable: React.FC<FinancialMetricsTableProps> = ({ value, onChange, fallback }) => {
  const fb = fallback ?? DEFAULT_PROJECT_YEARLY_COSTS;

  const [inputs, setInputs] = useState<FinancialInputs>(() => {
    const normalized = normalizeYearlyCosts(value, fb);
    return { software: [...normalized.software], services: [...normalized.services], partnerServices: [...normalized.partnerServices], ramp: [...normalized.ramp] };
  });

  const [growthRate, setGrowthRate] = useState<number>(() => {
    const normalized = normalizeYearlyCosts(value, fb);
    return normalized.growthRate;
  });

  const [inflationRate, setInflationRate] = useState<number>(() => {
    const normalized = normalizeYearlyCosts(value, fb);
    return normalized.inflationRate;
  });

  useEffect(() => {
    const normalized = normalizeYearlyCosts(value, fb);
    setInputs(prev => {
      const matchesSoftware = normalized.software.every((val, idx) => val === prev.software[idx]);
      const matchesServices = normalized.services.every((val, idx) => val === prev.services[idx]);
      const matchesPartner = normalized.partnerServices.every((val, idx) => val === prev.partnerServices[idx]);
      const matchesRamp = normalized.ramp.every((val, idx) => val === prev.ramp[idx]);
      if (matchesSoftware && matchesServices && matchesPartner && matchesRamp) {
        return prev;
      }
      return { software: [...normalized.software], services: [...normalized.services], partnerServices: [...normalized.partnerServices], ramp: [...normalized.ramp] };
    });
    setGrowthRate(normalized.growthRate);
    setInflationRate(normalized.inflationRate);
  }, [value, fallback]);

  const totals = useMemo(
    () => FINANCIAL_YEARS.map((_, idx) => (inputs.software[idx] || 0) + (inputs.services[idx] || 0) + (inputs.partnerServices[idx] || 0)),
    [inputs]
  );

  const emitChange = (nextInputs: FinancialInputs, nextGrowth?: number, nextInflation?: number) => {
    if (!onChange) return;
    const nextTotals = FINANCIAL_YEARS.map((_, idx) => (nextInputs.software[idx] || 0) + (nextInputs.services[idx] || 0) + (nextInputs.partnerServices[idx] || 0));
    onChange({
      software: [...nextInputs.software],
      services: [...nextInputs.services],
      partnerServices: [...nextInputs.partnerServices],
      total: nextTotals,
      ramp: [...nextInputs.ramp],
      growthRate: nextGrowth ?? growthRate,
      inflationRate: nextInflation ?? inflationRate,
    });
  };

  const handleFinancialInputChange = (row: 'software' | 'services' | 'partnerServices', index: number, value: string) => {
    setInputs(prev => {
      const nextRow = [...prev[row]];
      nextRow[index] = parseCurrencyValue(value);
      const nextInputs = { ...prev, [row]: nextRow };
      emitChange(nextInputs);
      return nextInputs;
    });
  };

  const handleRampChange = (index: number, value: string) => {
    setInputs(prev => {
      const nextRamp = [...prev.ramp];
      const parsed = parseInt(value.replace(/[^0-9]/g, ''), 10);
      nextRamp[index] = Number.isFinite(parsed) ? Math.max(0, Math.min(100, parsed)) : 0;
      const nextInputs = { ...prev, ramp: nextRamp };
      emitChange(nextInputs);
      return nextInputs;
    });
  };

  const handleGrowthRateChange = (value: string) => {
    const parsed = parseFloat(value) || 0;
    setGrowthRate(parsed);
    emitChange(inputs, parsed, undefined);
  };

  const handleInflationRateChange = (value: string) => {
    const parsed = parseFloat(value) || 0;
    setInflationRate(parsed);
    emitChange(inputs, undefined, parsed);
  };

  const gridColumns = `minmax(120px, 1fr) repeat(${FINANCIAL_YEARS.length}, minmax(100px, 1fr))`;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4 space-y-4">
      {/* Growth Rate & Inflation Rate */}
      <div className="flex flex-wrap gap-6">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">Growth Rate</label>
          <div className="relative w-24">
            <Input
              type="number"
              step="0.1"
              value={growthRate}
              onChange={(e) => handleGrowthRateChange(e.target.value)}
              className="h-9 pr-7 text-right border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">Inflation Rate</label>
          <div className="relative w-24">
            <Input
              type="number"
              step="0.1"
              value={inflationRate}
              onChange={(e) => handleInflationRateChange(e.target.value)}
              className="h-9 pr-7 text-right border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
          </div>
        </div>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400">
        Provide projected software and services values. Totals update automatically for each year.
      </p>
      <div className="overflow-x-auto">
        <div className="min-w-[640px] space-y-4">
          {/* Header row */}
          <div
            className="grid gap-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            style={{ gridTemplateColumns: gridColumns }}
          >
            <div className="text-left">&nbsp;</div>
            {FINANCIAL_YEARS.map(year => (
              <div key={year} className="text-center">{year}</div>
            ))}
          </div>
          <div className="space-y-3">
            {/* Software, Services & Partner rows */}
            {([
              { key: 'software' as const, label: 'Software' },
              { key: 'services' as const, label: 'Services' },
              { key: 'partnerServices' as const, label: 'Partner Fees' },
            ]).map(({ key, label }) => (
              <div
                key={key}
                className="grid items-center"
                style={{ gridTemplateColumns: gridColumns, gap: '12px' }}
              >
                <div className="font-medium text-gray-700 dark:text-gray-200">{label}</div>
                {FINANCIAL_YEARS.map((year, index) => (
                  <Input
                    key={`${key}-${year}`}
                    data-cy={`financial-input-${key}-year-${index}`}
                    type="text"
                    value={(inputs[key][index] ?? 0) > 0 ? formatCurrency(inputs[key][index] ?? 0) : ''}
                    onChange={(event) => handleFinancialInputChange(key, index, event.target.value)}
                    onFocus={(event) => {
                      if (event.target.value === '$0') event.target.value = '';
                    }}
                    onBlur={(event) => {
                      if (event.target.value === '') {
                        handleFinancialInputChange(key, index, '0');
                      }
                    }}
                    placeholder="$0"
                    className="h-10 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 text-right pr-4"
                  />
                ))}
              </div>
            ))}
            {/* Total row */}
            <div
              className="grid items-center"
              style={{ gridTemplateColumns: gridColumns, gap: '12px' }}
            >
              <div className="font-semibold text-gray-900 dark:text-white">Total</div>
              {totals.map((total, index) => (
                <div
                  key={`total-${FINANCIAL_YEARS[index]}`}
                  className="h-10 flex items-center justify-end rounded-md bg-white dark:bg-gray-950 border border-dashed border-primary/40 px-3 text-sm font-semibold text-primary-700 dark:text-primary-300"
                >
                  {formatCurrency(total)}
                </div>
              ))}
            </div>
            {/* Ramp row */}
            <div
              className="grid items-center"
              style={{ gridTemplateColumns: gridColumns, gap: '12px' }}
            >
              <div className="font-medium text-gray-700 dark:text-gray-200">Ramp %</div>
              {FINANCIAL_YEARS.map((year, index) => (
                <div key={`ramp-${year}`} className="relative">
                  <Input
                    data-cy={`financial-input-ramp-year-${index}`}
                    type="number"
                    min={0}
                    max={100}
                    value={inputs.ramp[index] ?? 100}
                    onChange={(e) => handleRampChange(index, e.target.value)}
                    className="h-10 pr-7 text-right border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialMetricsTable;
